/**
 * ThemeEditorModal - 챗봇 테마 설정 공통 모달
 * WorkflowBuilderPage와 ChatPage 양쪽에서 사용
 */

import { useState, useEffect } from 'react';
import { Button, Modal } from '../ui';
import ArrowUpIcon from '../../assets/icons/common/arrow-up.svg?react';
import CloseIcon from '../../assets/icons/common/close.svg?react';
// 챗봇 아이콘 (대화 화면과 동일) - 선 색 = 챗봇 아이콘 색상(기본 흰색), 바탕 = 아이콘 배경 색상 (없으면 메인 색상)
import { BotGlyph as BotIcon } from './BotGlyph';

export interface ChatbotThemeData {
  botName?: string;
  primaryColor?: string;
  backgroundColor?: string;
  botIconColor?: string;     // 챗봇 아이콘 배경 색상
  botIconFgColor?: string;   // 챗봇 아이콘(svg) 색상
  botIconStyle?: 'white' | 'black';
  welcomeMessage?: string;
  userBubbleColor?: string;
  userTextColor?: string;
  botBubbleColor?: string;
  botTextColor?: string;
  quickPrompts?: string[];
}

interface ThemeColorFieldProps {
  label: string;
  value?: string;
  fallback: string;
  onChange: (value: string) => void;
}

const ThemeColorField = ({ label, value, fallback, onChange }: ThemeColorFieldProps) => (
  <div className="wb-theme-field">
    <label>{label}</label>
    <div className="wb-theme-color">
      <span className="wb-theme-swatch">
        <input type="color" value={value || fallback} onChange={(e) => onChange(e.target.value)} />
      </span>
      <input type="text" className="wb-theme-input" placeholder={fallback} value={value || ''} onChange={(e) => onChange(e.target.value)} />
    </div>
  </div>
);

// 카드 제목 - 우측 화살표로 접기/펼치기
const CardTitle = ({ title, collapsed, onToggle }: { title: string; collapsed: boolean; onToggle: () => void }) => (
  <h4 className="wb-theme-card-title">
    <button type="button" className="wb-theme-card-toggle" aria-expanded={!collapsed} onClick={onToggle}>
      {title}
      <svg className="wb-theme-card-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="m6 9 6 6 6-6" />
      </svg>
    </button>
  </h4>
);

interface ThemeEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (theme: ChatbotThemeData) => Promise<void>;
  initialTheme: ChatbotThemeData;
  botName?: string; // 미리보기용 이름 (저장된 이름 없을 때 fallback)
  saving?: boolean;
  showQuickPrompts?: boolean; // 빠른 질문 섹션 노출 여부 (관리자 또는 본인 챗봇만)
}

export function ThemeEditorModal({
  isOpen,
  onClose,
  onSave,
  initialTheme,
  botName = '챗봇',
  saving = false,
  showQuickPrompts = false,
}: ThemeEditorModalProps) {
  const [tempTheme, setTempTheme] = useState<ChatbotThemeData>(initialTheme);
  const [isDragging, setIsDragging] = useState(false);
  const [modalPos, setModalPos] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  // 설정 카드 접힘 상태 (basic = 기본 설정, bubble = 말풍선 스타일, prompts = 빠른 질문)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggleCard = (key: string) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  // isOpen이 true로 바뀔 때 초기화
  useEffect(() => {
    if (isOpen) {
      setTempTheme(initialTheme);
      setModalPos({ x: 0, y: 0 });
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDragStart = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - modalPos.x, y: e.clientY - modalPos.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setModalPos({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleSave = async () => {
    const cleanedTheme = {
      ...tempTheme,
      quickPrompts: (tempTheme.quickPrompts || []).filter(p => p.trim()),
    };
    await onSave(cleanedTheme);
  };

  return (
    <div onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="챗봇 테마 설정"
        size="lg"
        className="wb-theme-modal"
        style={{ transform: `translate(${modalPos.x}px, ${modalPos.y}px)` }}
        headerProps={{ style: { cursor: 'move' }, onMouseDown: handleDragStart }}
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={onClose}>취소</Button>
            <Button color="primary" onClick={handleSave} disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </Button>
          </>
        }
      >
        <div className="wb-theme-body">
          {/* 좌측: 설정 */}
          <div className="wb-theme-settings">
            {/* 기본 설정 */}
            <section className={`wb-theme-card${collapsed.basic ? ' is-collapsed' : ''}`}>
              <CardTitle title="기본 설정" collapsed={!!collapsed.basic} onToggle={() => toggleCard('basic')} />
              <div className='wb-theme-card-body'>
                <div className="wb-theme-field">
                  <label>챗봇 이름</label>
                  <input
                    type="text"
                    className="input--md wb-theme-input"
                    placeholder="예: 출장 도우미"
                    value={tempTheme.botName || ''}
                    onChange={(e) => setTempTheme({ ...tempTheme, botName: e.target.value })}
                  />
                </div>
                <div className="wb-theme-row">
                  <ThemeColorField label="메인 색상" value={tempTheme.primaryColor} fallback="#6366f1"
                    onChange={(v) => setTempTheme({ ...tempTheme, primaryColor: v })} />
                  <ThemeColorField label="배경 색상" value={tempTheme.backgroundColor} fallback="#f8fafc"
                    onChange={(v) => setTempTheme({ ...tempTheme, backgroundColor: v })} />
                </div>
                <div className="wb-theme-row">
                  <ThemeColorField label="챗봇 아이콘 색상" value={tempTheme.botIconFgColor} fallback="#ffffff"
                    onChange={(v) => setTempTheme({ ...tempTheme, botIconFgColor: v })} />
                  <ThemeColorField label="챗봇 아이콘 배경 색상" value={tempTheme.botIconColor} fallback="#6366f1"
                    onChange={(v) => setTempTheme({ ...tempTheme, botIconColor: v })} />
                </div>
                <div className="wb-theme-field">
                  <label>환영 메시지</label>
                  <textarea className="wb-theme-input" rows={3} placeholder="안녕하세요! 무엇이든 물어보세요."
                    value={tempTheme.welcomeMessage || ''}
                    onChange={(e) => setTempTheme({ ...tempTheme, welcomeMessage: e.target.value })} />
                </div>
              </div>
            </section>

            {/* 말풍선 스타일 */}
            <section className={`wb-theme-card${collapsed.bubble ? ' is-collapsed' : ''}`}>
              <CardTitle title="말풍선 스타일" collapsed={!!collapsed.bubble} onToggle={() => toggleCard('bubble')} />
              <div className='wb-theme-card-body'>
                <div className="wb-theme-row">
                  <ThemeColorField label="질문 배경색" value={tempTheme.userBubbleColor} fallback="#e0e7ff"
                    onChange={(v) => setTempTheme({ ...tempTheme, userBubbleColor: v })} />
                  <ThemeColorField label="질문 글자색" value={tempTheme.userTextColor} fallback="#1e1b4b"
                    onChange={(v) => setTempTheme({ ...tempTheme, userTextColor: v })} />
                </div>
                <div className="wb-theme-row">
                  <ThemeColorField label="답변 배경색" value={tempTheme.botBubbleColor} fallback="#ffffff"
                    onChange={(v) => setTempTheme({ ...tempTheme, botBubbleColor: v })} />
                  <ThemeColorField label="답변 글자색" value={tempTheme.botTextColor} fallback="#374151"
                    onChange={(v) => setTempTheme({ ...tempTheme, botTextColor: v })} />
                </div>
              </div>
            </section>

            {/* 빠른 질문 */}
            {showQuickPrompts && (
            <section className={`wb-theme-card${collapsed.prompts ? ' is-collapsed' : ''}`}>
              <CardTitle title="빠른 질문" collapsed={!!collapsed.prompts} onToggle={() => toggleCard('prompts')} />
              <div className='wb-theme-card-body'>
                {(tempTheme.quickPrompts || ['']).map((prompt, index) => (
                  <div key={index} className="wb-theme-prompt-row">
                    <input type="text" className="wb-theme-input" placeholder={`질문 ${index + 1}`} value={prompt}
                      onChange={(e) => {
                        const next = [...(tempTheme.quickPrompts || [''])];
                        next[index] = e.target.value;
                        setTempTheme({ ...tempTheme, quickPrompts: next });
                      }} />
                    <button type="button" className="wb-theme-prompt-del" title="삭제" aria-label="삭제"
                      onClick={() => {
                        const next = (tempTheme.quickPrompts || ['']).filter((_, i) => i !== index);
                        setTempTheme({ ...tempTheme, quickPrompts: next.length ? next : [''] });
                      }}>
                      <CloseIcon />
                    </button>
                  </div>
                ))}
                <button type="button" className="wb-theme-prompt-add"
                  onClick={() => setTempTheme({ ...tempTheme, quickPrompts: [...(tempTheme.quickPrompts || ['']), ''] })}>
                  + 질문 추가
                </button>
              </div>
            </section>
            )}
          </div>

          {/* 우측: 미리보기 */}
          <div className="wb-theme-preview">
            <div className="wb-theme-preview-label">미리보기</div>
            {/* 채팅 창 미리보기 - 색은 모두 테마 설정값 (없으면 기본색) */}
            <div
              className="wb-theme-preview-chat"
              style={{ '--preview-primary': tempTheme.primaryColor || '#736ce3' } as React.CSSProperties}
            >
              {/* 헤더 (배경 = 메인 색상): 챗봇 아이콘 · 이름 · 온라인 */}
              <div className="wb-theme-preview-head" style={{ background: tempTheme.primaryColor || '#736ce3' }}>
                {/* 챗봇 아이콘 - 바탕 = 챗봇 아이콘 색 (없으면 메인 색상) */}
                <span
                  className="wb-theme-preview-avatar"
                  style={{ background: tempTheme.botIconColor || tempTheme.primaryColor || 'rgba(255,255,255,0.1)', color: tempTheme.botIconFgColor || '#fff' }}
                >
                  <BotIcon />
                </span>
                <span className="wb-theme-preview-info">
                  <span className="wb-theme-preview-name">{tempTheme.botName || botName}</span>
                  <span className="wb-theme-preview-status">온라인</span>
                </span>
              </div>

              <div className="wb-theme-preview-messages" style={{ background: tempTheme.backgroundColor || '#f8fafc' }}>
                {/* 사용자 질문 (오른쪽) */}
                <div className="wb-theme-msg user">
                  <div className="wb-theme-bubble user" style={{ background: tempTheme.userBubbleColor || '#e0e7ff', color: tempTheme.userTextColor || '#1e1b4b' }}>
                    출장 규정이 어떻게 되나요?
                  </div>
                </div>
                {/* 챗봇 답변 (작은 아이콘 + 말풍선) */}
                <div className="wb-theme-msg bot">
                  <span
                    className="wb-theme-msg-avatar"
                    style={{ background: tempTheme.botIconColor || tempTheme.primaryColor || '#6366f1', color: tempTheme.botIconFgColor || '#fff' }}
                  >
                    <BotIcon />
                  </span>
                  <div className="wb-theme-bubble bot" style={{ background: tempTheme.botBubbleColor || '#ffffff', color: tempTheme.botTextColor || '#374151' }}>
                    {tempTheme.welcomeMessage || '안녕하세요! 무엇이든 물어보세요.'}
                  </div>
                </div>
                {/* 빠른 질문 (답변 말풍선 줄에 맞춰 들여씀) */}
                <div className="wb-theme-chips">
                  {((tempTheme.quickPrompts || []).filter(p => p.trim()).length > 0
                    ? tempTheme.quickPrompts!.filter(p => p.trim()).slice(0, 3)
                    : ['질문 예시 1', '질문 예시 2']
                  ).map((prompt, i) => (
                    <span key={i} className="wb-theme-chip">
                      {prompt.length > 20 ? prompt.slice(0, 20) + '...' : prompt}
                    </span>
                  ))}
                </div>

                {/* 입력창 (아래 고정) */}
                <div className="wb-theme-preview-input">
                  <span>메시지를 입력하세요...</span>
                  <span className="wb-theme-send"><ArrowUpIcon /></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default ThemeEditorModal;
